import { call, put, takeLatest } from 'redux-saga/effects';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';
import SuperFetch from 'library/helpers/superFetch';
import describeApiError from 'library/helpers/describeApiError';
import { Notification } from 'zComponents/atoms';
import { accessWindowsActions } from './slice';

// The server's refusal: `error` when set, else its `info` sentence (e.g. why a site move was blocked).
const failureMessage = (res, fallback) => describeApiError(res, null) || res.info || fallback;

// ── Load all access windows ──
function* getAccessWindows() {
	try {
		const response = yield call(() => SuperFetch.get('/accessWindows'));

		if (response.statusCode === 200) {
			yield put(accessWindowsActions.loadAccessWindowsSuccess(response.data));
		} else {
			throw new Error(response.error?.message || 'Failed to load access windows');
		}
	} catch (error) {
		yield put(accessWindowsActions.loadAccessWindowsError(error.message));
	}
}

// ── Create access window ──
function* createAccessWindow({ payload }) {
	try {
		const res = yield call(() => SuperFetch.post('/accessWindows', payload));

		if (res.statusCode === 200) {
			yield put(accessWindowsActions.createAccessWindowSuccess());
			Notification('success', 'Schedule created successfully.');
			yield put(accessWindowsActions.loadAccessWindows());
		} else {
			throw new Error(failureMessage(res, 'Failed to create schedule.'));
		}
	} catch (error) {
		yield put(accessWindowsActions.createAccessWindowError(error.message));
		Notification('error', error.message);
	}
}

// ── Update access window ──
function* updateAccessWindow({ payload }) {
	try {
		const { accessWindowID, ...data } = payload;
		const res = yield call(() => SuperFetch.put(`/accessWindows/${accessWindowID}`, data));

		if (res.statusCode === 200) {
			yield put(accessWindowsActions.updateAccessWindowSuccess());
			Notification('success', 'Schedule updated successfully.');
			if (res.data?.warnings?.length) {
				res.data.warnings.forEach((w) => Notification('info', w));
			}
			yield put(accessWindowsActions.loadAccessWindows());
		} else {
			throw new Error(failureMessage(res, 'Failed to update schedule.'));
		}
	} catch (error) {
		yield put(accessWindowsActions.updateAccessWindowError(error.message));
		Notification('error', error.message);
	}
}

// ── Delete access window ──
function* deleteAccessWindow({ payload }) {
	try {
		const { accessWindowID } = payload;
		const res = yield call(() => SuperFetch.delete(`/accessWindows/${accessWindowID}`));

		if (res.statusCode === 200) {
			yield put(accessWindowsActions.deleteAccessWindowSuccess());
			Notification('success', 'Schedule deleted successfully.');
			yield put(accessWindowsActions.loadAccessWindows());
		} else {
			throw new Error(res.error?.message || res.error || 'Failed to delete schedule.');
		}
	} catch (error) {
		yield put(accessWindowsActions.deleteAccessWindowError(error.message));
		Notification('error', error.message);
	}
}

// ── Load external IR TimePeriods (for duplicate detection + link picker) ──
function* loadExternalTimePeriods() {
	try {
		const res = yield call(() =>
			SuperFetch.get(
				'/accessWindows/external',
				{},
				{ integrationID: INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id }
			)
		);

		if (res.statusCode === 200) {
			yield put(accessWindowsActions.loadExternalTimePeriodsSuccess(res.data));
		} else {
			yield put(accessWindowsActions.loadExternalTimePeriodsError());
		}
	} catch {
		yield put(accessWindowsActions.loadExternalTimePeriodsError());
	}
}

// ── Link access window to IR TimePeriod ──
function* linkToIR({ payload }) {
	try {
		const { accessWindowID, externalAddress, externalName, externalID } = payload;
		const res = yield call(() =>
			SuperFetch.post(`/accessWindows/${accessWindowID}/integrations`, {
				integrationID: 1,
				externalAddress,
				externalName,
				externalID
			})
		);

		if (res.statusCode === 200) {
			yield put(accessWindowsActions.linkToIRSuccess());
			Notification('success', 'Schedule linked to Integriti Time Period.');
			yield put(accessWindowsActions.loadAccessWindows());
			yield put(accessWindowsActions.loadExternalTimePeriods());
		} else {
			throw new Error(res.error?.message || 'Failed to link schedule.');
		}
	} catch (error) {
		yield put(accessWindowsActions.linkToIRError());
		Notification('error', error.message);
	}
}

export default function* accessWindowsSagas() {
	yield takeLatest(accessWindowsActions.loadAccessWindows, getAccessWindows);
	yield takeLatest(accessWindowsActions.createAccessWindow, createAccessWindow);
	yield takeLatest(accessWindowsActions.updateAccessWindow, updateAccessWindow);
	yield takeLatest(accessWindowsActions.deleteAccessWindow, deleteAccessWindow);
	yield takeLatest(accessWindowsActions.loadExternalTimePeriods, loadExternalTimePeriods);
	yield takeLatest(accessWindowsActions.linkToIR, linkToIR);
}
